import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, XCircle, ArrowClockwise, EnvelopeSimple, CircleNotch, Warning } from "@phosphor-icons/react";

const dmY = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const STATUS_TONE = {
    sent:      { icon: CheckCircle, cls: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Sent" },
    delivered: { icon: CheckCircle, cls: "text-emerald-800 bg-emerald-100 border-emerald-300", label: "Delivered" },
    sending:   { icon: CircleNotch, cls: "text-slate-700 bg-slate-100 border-slate-300 [&_svg]:animate-spin", label: "Sending" },
    retrying:  { icon: ArrowClockwise, cls: "text-amber-800 bg-amber-100 border-amber-300 [&_svg]:animate-spin", label: "Retrying" },
    failed:    { icon: XCircle,     cls: "text-rose-700 bg-rose-50 border-rose-200",            label: "Failed" },
    bounced:   { icon: Warning,     cls: "text-amber-800 bg-amber-50 border-amber-300",         label: "Bounced" },
};

export default function EmailLog() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [retrying, setRetrying] = useState(null);
    const [filters, setFilters] = useState({ party_name: "", status: "all", date_from: "", date_to: "" });

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.party_name.trim()) params.party_name = filters.party_name.trim();
            if (filters.status && filters.status !== "all") params.status = filters.status;
            if (filters.date_from) params.date_from = filters.date_from;
            if (filters.date_to) params.date_to = filters.date_to;
            params.limit = 500;
            const { data } = await api.get("/email-log", { params });
            setRows(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    const retry = async (id) => {
        setRetrying(id);
        try {
            const { data } = await api.post(`/email-log/${id}/retry`);
            toast.success(`Resent to ${data.sent_to}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRetrying(null); }
    };

    const summary = useMemo(() => {
        const s = { sent: 0, delivered: 0, failed: 0, bounced: 0 };
        for (const r of rows) if (s[r.status] != null) s[r.status] += 1;
        return s;
    }, [rows]);

    return (
        <div className="space-y-6" data-testid="admin-email-log-page">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Administration</div>
                <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
                    <EnvelopeSimple size={32} weight="fill" className="text-blue-700" />
                    Email Log
                </h1>
                <p className="mt-1 text-sm text-slate-600">
                    Every email this workspace has sent — welcome logins, statement PDFs, retries. Scoped to your company only.
                </p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatCard label="Sent" value={summary.sent + summary.delivered} tone="emerald" />
                <StatCard label="Failed" value={summary.failed} tone="rose" />
                <StatCard label="Bounced" value={summary.bounced} tone="amber" />
                <StatCard label="Total" value={rows.length} tone="slate" />
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-4 sm:items-end">
                    <div>
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Party name</label>
                        <Input
                            data-testid="email-log-filter-party"
                            value={filters.party_name}
                            onChange={(e) => setFilters((f) => ({ ...f, party_name: e.target.value }))}
                            placeholder="e.g. Sharma Traders"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Status</label>
                        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                            <SelectTrigger data-testid="email-log-filter-status" className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All statuses</SelectItem>
                                <SelectItem value="sent">Sent</SelectItem>
                                <SelectItem value="delivered">Delivered</SelectItem>
                                <SelectItem value="failed">Failed</SelectItem>
                                <SelectItem value="bounced">Bounced</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">From</label>
                        <Input
                            data-testid="email-log-filter-from"
                            type="date"
                            value={filters.date_from}
                            onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">To</label>
                        <Input
                            data-testid="email-log-filter-to"
                            type="date"
                            value={filters.date_to}
                            onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                            className="mt-1"
                        />
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                    <Button
                        onClick={load}
                        disabled={loading}
                        data-testid="email-log-apply"
                        className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                        {loading ? "Loading…" : "Apply filters"}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => { setFilters({ party_name: "", status: "all", date_from: "", date_to: "" }); setTimeout(load, 0); }}
                        data-testid="email-log-reset"
                    >
                        Reset
                    </Button>
                </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="email-log-table">
                        <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600">
                            <tr>
                                <th className="px-3 py-2 text-left">When</th>
                                <th className="px-3 py-2 text-left">Party</th>
                                <th className="px-3 py-2 text-left">Recipient</th>
                                <th className="px-3 py-2 text-left">Subject</th>
                                <th className="px-3 py-2 text-left">Status</th>
                                <th className="px-3 py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Loading email history…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400" data-testid="email-log-empty">
                                    No emails match these filters.
                                </td></tr>
                            ) : rows.map((r) => {
                                const tone = STATUS_TONE[r.status] || { icon: CircleNotch, cls: "text-slate-700 bg-slate-100 border-slate-200", label: r.status || "Unknown" };
                                const Icon = tone.icon;
                                const retryable = r.status === "failed" || r.status === "bounced";
                                return (
                                    <tr key={r.id} data-testid={`email-log-row-${r.id}`} className={r.status === "failed" ? "bg-rose-50/30" : r.status === "bounced" ? "bg-amber-50/30" : ""}>
                                        <td className="px-3 py-2 whitespace-nowrap text-slate-600 font-mono text-xs">{dmY(r.sent_at)}</td>
                                        <td className="px-3 py-2 text-slate-800 font-medium">{r.party_name || <span className="italic text-slate-400">system</span>}</td>
                                        <td className="px-3 py-2 text-slate-700">{r.recipient}</td>
                                        <td className="px-3 py-2 text-slate-600 max-w-[300px] truncate" title={r.subject || ""}>{r.subject || <span className="text-slate-400">—</span>}</td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-semibold uppercase ${tone.cls}`}>
                                                <Icon size={12} weight="fill" /> {tone.label}
                                                {(r.retry_count || 0) > 0 && (
                                                    <span
                                                        className="ml-1 rounded bg-white/70 px-1 text-[9px] font-mono tracking-tight text-amber-900"
                                                        title={`Retried ${r.retry_count} time${r.retry_count === 1 ? "" : "s"}${r.last_retry_reason ? ` — ${r.last_retry_reason}` : ""}`}
                                                        data-testid={`email-log-retry-count-${r.id}`}
                                                    >
                                                        ×{r.retry_count}
                                                    </span>
                                                )}
                                            </span>
                                            {r.error && (r.status === "failed" || r.status === "bounced") && (
                                                <div className="mt-0.5 max-w-[300px] truncate text-[10px] text-rose-600" title={r.error}>{r.error}</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            {retryable && (
                                                <button
                                                    type="button"
                                                    onClick={() => retry(r.id)}
                                                    disabled={retrying === r.id}
                                                    data-testid={`email-log-retry-${r.id}`}
                                                    className="inline-flex items-center gap-1 rounded border border-blue-300 bg-white px-2 py-1 text-[11px] font-semibold text-blue-800 hover:bg-blue-50 disabled:opacity-50"
                                                >
                                                    {retrying === r.id ? <CircleNotch size={11} className="animate-spin" /> : <ArrowClockwise size={11} weight="bold" />}
                                                    Resend
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value, tone }) {
    const toneCls = {
        emerald: "text-emerald-700",
        rose: "text-rose-700",
        amber: "text-amber-700",
        slate: "text-slate-900",
    }[tone] || "text-slate-900";
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</div>
            <div className={`mt-2 font-display text-2xl font-bold tabular-nums ${toneCls}`}>{value}</div>
        </div>
    );
}
